import { Eye, ImageIcon, Trash2 } from 'lucide-react';
import {
  firstGalleryMedia,
  galleryStatusChangedByText,
  isGalleryVideo,
  type GalleryItem,
} from '@/features/gallery/types/gallery';

type GalleryCardProps = {
  item: GalleryItem;
  canDelete: boolean;
  showDelete: boolean;
  onViewDetail: (item: GalleryItem) => void;
  onDelete: (item: GalleryItem) => void;
};

export function GalleryCard({ item, canDelete, showDelete, onViewDetail, onDelete }: GalleryCardProps) {
  const media = firstGalleryMedia(item);
  const url = media?.url;
  const video = isGalleryVideo(media);
  const status = String(item.status || 'PENDING').toUpperCase();
  const statusChangedBy = galleryStatusChangedByText(item);
  const statusClass =
    status === 'APPROVE'
      ? 'bg-emerald-500/90'
      : status === 'REJECT'
        ? 'bg-red-500/90'
        : 'bg-amber-500/90';
  return (
    <article className="group overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-white shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-20px_rgba(16,38,73,0.4)]">
      <div className="relative h-44 overflow-hidden bg-[var(--dash-soft)]">
        {!url ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-[var(--dash-muted)]">
            <ImageIcon className="h-8 w-8 opacity-40" />
            <span className="text-xs font-medium">No preview</span>
          </div>
        ) : video ? (
          <video
            src={url}
            muted
            playsInline
            preload="metadata"
            onMouseEnter={(e) => void e.currentTarget.play()}
            onMouseLeave={(e) => {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
            }}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
        ) : (
          <img
            src={url}
            alt={item.contentName || 'Gallery item'}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
        )}

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/35 to-transparent" />

        <span
          className={`absolute top-3 left-3 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide text-white uppercase ${statusClass}`}
        >
          {status}
        </span>

        {video ? (
          <span className="absolute bottom-3 left-3 rounded-md bg-black/50 px-2 py-1 text-[11px] font-medium text-white">
            Video
          </span>
        ) : null}

        <button
          type="button"
          onClick={() => onViewDetail(item)}
          className="absolute top-3 right-3 rounded-lg bg-white/90 p-1.5 text-[var(--dash-ink)] opacity-0 shadow-sm transition group-hover:opacity-100"
          aria-label="View details"
        >
          <Eye className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 px-3.5 py-3">
        <div className="min-w-0 text-left">
          <h3
            className="m-0 truncate text-sm font-semibold text-[var(--dash-ink)]"
            title={item.contentName}
          >
            {item.contentName || 'Untitled'}
          </h3>
          {statusChangedBy ? (
            <p
              className="mt-0.5 mb-0 truncate text-[11px] text-[var(--dash-muted)]"
              title={statusChangedBy}
            >
              {statusChangedBy}
            </p>
          ) : null}
        </div>
        {canDelete && showDelete ? (
          <button
            type="button"
            onClick={() => onDelete(item)}
            className="rounded-lg p-1.5 text-[var(--dash-muted)] transition hover:bg-red-50 hover:text-red-600"
            aria-label="Delete content"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </article>
  );
}
